import { useEffect, useState } from 'react'
import { Clock, Flag, MapPinOff } from 'lucide-react'
import { formatAge } from '@/components/map/camera-sphere/formatAge'
import type { AthleteProfile } from '@/lib/watch/broadcastData'
import type { EventAthlete } from '@/lib/watch/eventAthletes'
import type { AthleteGraphic } from '@/lib/watch/graphics'
import { raceById, type Race } from '@/lib/watch/races'
import { useServerNow } from '@/lib/watch/useServerNow'
import { useAthleteProfile } from '@/lib/watch/useStandings'
import type { BroadcastElementProps } from './context'
import { Bib, Place, Portrait, PreviewMark, Progress, RaceTag, formatKm, shortAge } from './primitives'
import './broadcast.css'

export interface AthleteLowerThirdViewProps {
  /** Who: their profile, or the roster's row until it has loaded. */
  athlete: EventAthlete
  /** Their progress, place and newest fix; null until it has loaded (the card names them meanwhile). */
  profile: AthleteProfile | null
  race: Race | null
  /** Now on the server's clock, in epoch milliseconds: ages are read from it. */
  now: number
  /** One line over the video (a phone upright). */
  compact?: boolean
  preview?: boolean
}

/** How fresh their fix is: a glyph with a short age ("4m"), and the same in words for assistive technology. */
function freshness(profile: AthleteProfile, now: number): { icon: keyof typeof GLYPH; short: string | null; words: string; stale: boolean } {
  if (profile.progress?.finishedAt) return { icon: 'flag', short: null, words: 'Finished', stale: false }
  if (!profile.position) return { icon: 'clock', short: '–', words: 'Not seen yet', stale: true }
  const ms = now - profile.position.recordedAt
  const age = formatAge(ms)
  const seen = profile.stale ? `Last seen ${age}` : `Seen ${age}`
  if (profile.progress?.offCourse) return { icon: 'off', short: shortAge(ms), words: `Off course · ${seen}`, stale: profile.stale }
  return { icon: 'clock', short: shortAge(ms), words: seen, stale: profile.stale }
}

const GLYPH = { clock: Clock, flag: Flag, off: MapPinOff } as const

/** Their freshness as the card shows it: the glyph and the short age, said in words. */
function Freshness({ fresh }: { fresh: ReturnType<typeof freshness> }) {
  const Glyph = GLYPH[fresh.icon]
  return (
    <span className="bc-lower__fresh" data-stale={fresh.stale || undefined}>
      <Glyph aria-hidden="true" className="bc-lower__glyph" />
      {fresh.short && <span aria-hidden="true">{fresh.short}</span>}
      <span className="sr-only">{fresh.words}</span>
    </span>
  )
}

/**
 * An athlete's card in the lower third (docs/features/badger-cohost.md, Elements), icons over words: photo or initials,
 * name and place ("2 ♀", said "2nd woman"); then bib ("#434"), their race in its course colour, a bar of how far along it
 * they are beside the kilometres, and the clock with how fresh that is ("4m"). Stale tints the age, a finisher carries
 * the flag, off course the crossed-out pin. Upright on a phone it is one line: photo, name, place and kilometres.
 */
export function AthleteLowerThirdView({ athlete, profile, race, now, compact = false, preview = false }: AthleteLowerThirdViewProps) {
  const progress = profile?.progress ?? null
  const length = progress && progress.toFinishKm !== null ? progress.km + progress.toFinishKm : (race?.distanceKm ?? null)
  const km = progress ? (progress.finishedAt && length !== null ? length : progress.km) : null
  const place = profile?.place ?? null
  const placeNumber = place ? (athlete.gender && place.gender !== null ? place.gender : place.overall) : null
  const placeGender = place && athlete.gender && place.gender !== null ? athlete.gender : null
  const fresh = profile ? freshness(profile, now) : null

  if (compact) {
    return (
      <div className="bc-lower bc-lower--line" role="group" aria-label={athlete.name} data-preview={preview || undefined} data-stale={fresh?.stale || undefined}>
        {preview && <PreviewMark />}
        <Portrait athlete={athlete} size="line" />
        <span className="bc-lower__name">{athlete.name}</span>{' '}
        {placeNumber !== null && <Place place={placeNumber} gender={placeGender} className="bc-place--line" />}{' '}
        {km !== null && (
          <span className="bc-lower__line-km">
            <span aria-hidden="true">{formatKm(km)} km</span>
            <span className="sr-only">{length !== null ? `${formatKm(km)} of ${formatKm(length)} km` : `${formatKm(km)} km`}</span>
          </span>
        )}
        {fresh && <span className="sr-only">{fresh.words}</span>}
      </div>
    )
  }

  return (
    <div className="bc-lower" role="group" aria-label={athlete.name} data-preview={preview || undefined} data-stale={fresh?.stale || undefined}>
      {preview && <PreviewMark />}
      <Portrait athlete={athlete} />
      <div className="bc-lower__body">
        <div className="bc-lower__head">
          <p className="bc-lower__name">{athlete.name}</p>{' '}
          {placeNumber !== null && <Place place={placeNumber} gender={placeGender} />}
        </div>
        <p className="bc-lower__meta">
          {athlete.bib && <Bib bib={athlete.bib} className="bc-lower__bib" />}{' '}
          {race && <RaceTag race={race} />}{' '}
          {km !== null && <Progress km={km} lengthKm={length} color={race?.color} />}{' '}
          {fresh && <Freshness fresh={fresh} />}
        </p>
      </div>
    </div>
  )
}

/** How long a card on air waits for the athlete's profile before it names them from the roster alone. */
export const PROFILE_WAIT_MS = 1_500

/**
 * The athlete element: the spec names the athlete, and the card reads their profile every 10 s while it shows. On air
 * it comes in whole, once the profile is in (or, if that is slow, after `PROFILE_WAIT_MS` from the roster, filling in
 * as the profile arrives), so it doesn't grow on screen; the host's preview names them from the roster at once. An
 * athlete the page can't find (not on the roster it holds, or the profile answers that there is none) draws nothing.
 */
export function AthleteLowerThird({ spec, context, preview = false }: BroadcastElementProps<AthleteGraphic>) {
  const { value: profile, missing } = useAthleteProfile(context.eventId, spec.athleteId)
  const now = useServerNow()
  const [waited, setWaited] = useState<string | null>(null)
  useEffect(() => {
    const timer = window.setTimeout(() => setWaited(spec.athleteId), PROFILE_WAIT_MS)
    return () => window.clearTimeout(timer)
  }, [spec.athleteId])
  const fromRoster = preview || waited === spec.athleteId ? (context.roster.get(spec.athleteId) ?? null) : null
  const athlete = profile ?? fromRoster
  if (missing || !athlete) return null
  return (
    <AthleteLowerThirdView
      athlete={athlete}
      profile={profile}
      race={raceById(context.races, athlete.routeId)}
      now={now}
      compact={context.compact}
      preview={preview}
    />
  )
}
